import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";

const Privacy = () => {
  const { i18n } = useTranslation();
  const lastUpdated = new Date("2025-07-02").toLocaleDateString(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <SeoTags
        title="Privacy Policy - onBookmarks | Your Data, Your Control"
        description="Learn how onBookmarks protects your privacy with zero-knowledge encryption, GDPR compliance, and transparent data practices. We can't see your encrypted bookmarks."
        keywords="privacy policy, data protection, zero-knowledge encryption, GDPR, COPPA, client-side encryption, data security, privacy rights, onBookmarks"
        canonical="https://onbookmarks.com/legal/privacy-policy/"
        localized={false}
      />

      {/* Enhanced WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Privacy Policy - onBookmarks",
            description:
              "Comprehensive privacy policy explaining how onBookmarks protects user data with zero-knowledge encryption and complies with GDPR and COPPA regulations.",
            url: "https://onbookmarks.com/legal/privacy-policy",
            isPartOf: {
              "@type": "WebSite",
              name: "onBookmarks",
              url: "https://onbookmarks.com",
            },
            lastReviewed: lastUpdated,
            about: {
              "@type": "Thing",
              name: "Data Protection and Privacy",
            },
            mentions: [
              {
                "@type": "Thing",
                name: "GDPR",
              },
              {
                "@type": "Thing",
                name: "COPPA",
              },
              {
                "@type": "Thing",
                name: "Zero-Knowledge Encryption",
              },
            ],
          }),
        }}
      />

      <div className="min-vh-100">
        {/* Hero Section */}
        <div className="position-relative overflow-hidden bg-gradient-blue text-white py-5">
          <div className="container py-lg-5">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center">
                <div className="d-inline-flex align-items-center bg-white bg-opacity-25 rounded-pill px-3 py-2 mb-4">
                  <i className="bi bi-shield-check me-2"></i>
                  <span className="small fw-medium">Updated {lastUpdated}</span>
                </div>

                <h1 className="display-4 fw-bold mb-4 text-white">
                  Your Privacy is Our Priority
                </h1>
                <p
                  className="lead opacity-75 mx-auto"
                  style={{ maxWidth: "42rem" }}
                >
                  We believe in complete transparency. Learn how we protect your
                  data with zero-knowledge encryption and comply with global
                  privacy laws.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Summary Cards */}
        <div
          className="container"
          style={{ marginTop: "-3rem", position: "relative", zIndex: 10 }}
        >
          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-lg h-100">
                <div className="card-body p-4">
                  <div
                    className="bg-success bg-opacity-10 rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <i className="bi bi-lock-fill fs-4 text-success"></i>
                  </div>
                  <h3 className="h5 fw-semibold mb-2">
                    Zero-Knowledge Encryption
                  </h3>
                  <p className="text-muted mb-0">
                    We can't see your encrypted bookmarks - only you have the
                    keys
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-lg h-100">
                <div className="card-body p-4">
                  <div
                    className="bg-primary bg-opacity-10 rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <i className="bi bi-person-check-fill fs-4 text-primary"></i>
                  </div>
                  <h3 className="h5 fw-semibold mb-2">
                    GDPR & COPPA Compliant
                  </h3>
                  <p className="text-muted mb-0">
                    Full compliance with international privacy regulations
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-lg h-100">
                <div className="card-body p-4">
                  <div
                    className="bg-purple bg-opacity-10 rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <i className="bi bi-eye-fill fs-4 text-purple"></i>
                  </div>
                  <h3 className="h5 fw-semibold mb-2">Transparent Practices</h3>
                  <p className="text-muted mb-0">
                    Clear about what we collect and how we use it
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container py-5">
          {/* Table of Contents */}
          <div className="bg-light rounded-3 p-4 mb-5">
            <h2 className="h5 fw-semibold mb-3 d-flex align-items-center">
              <i className="bi bi-file-text me-2 text-muted"></i>
              Quick Navigation
            </h2>
            <div className="row g-2">
              <div className="col-md-6">
                <a href="#what-we-collect" className="text-decoration-none">
                  → What We Collect
                </a>
              </div>
              <div className="col-md-6">
                <a href="#how-we-use" className="text-decoration-none">
                  → How We Use Your Data
                </a>
              </div>
              <div className="col-md-6">
                <a href="#encryption" className="text-decoration-none">
                  → Encryption & Security
                </a>
              </div>
              <div className="col-md-6">
                <a href="#your-rights" className="text-decoration-none">
                  → Your Rights
                </a>
              </div>
              <div className="col-md-6">
                <a href="#gdpr" className="text-decoration-none">
                  → GDPR Compliance
                </a>
              </div>
              <div className="col-md-6">
                <a href="#coppa" className="text-decoration-none">
                  → Children's Privacy (COPPA)
                </a>
              </div>
              <div className="col-md-6">
                <a href="#cookies" className="text-decoration-none">
                  → Cookies & Tracking
                </a>
              </div>
              <div className="col-md-6">
                <a href="#contact" className="text-decoration-none">
                  → Contact Us
                </a>
              </div>
            </div>
          </div>

          {/* Introduction */}
          <section className="mb-5">
            <p className="lead text-muted">
              Welcome to onBookmarks! We're committed to protecting your privacy
              and giving you control over your data. This policy explains in
              simple terms what information we collect, how we protect it, and
              what rights you have.
            </p>
            <p className="text-muted">
              <strong>The Short Version:</strong> We use zero-knowledge
              encryption for your private bookmarks, comply with GDPR and COPPA,
              and never sell your data. You can delete everything anytime.
            </p>
          </section>

          {/* What We Collect */}
          <section id="what-we-collect" className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-database fs-3 text-primary me-3"></i>
              What Information We Collect
            </h2>

            <div className="row g-4">
              <div className="col-12">
                <div className="bg-primary bg-opacity-10 rounded-3 p-4">
                  <h3 className="h5 fw-semibold mb-3 d-flex align-items-center">
                    <i className="bi bi-person-check text-primary me-2"></i>
                    Account Information
                  </h3>
                  <ul className="list-unstyled mb-0">
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2 flex-shrink-0"></i>
                      <span>
                        Email address (for login and important notifications)
                      </span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2 flex-shrink-0"></i>
                      <span>
                        Display name (optional, you choose what to share)
                      </span>
                    </li>
                    <li className="d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-success me-2 flex-shrink-0"></i>
                      <span>Account preferences and settings</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="col-12">
                <div className="bg-success bg-opacity-10 rounded-3 p-4">
                  <h3 className="h5 fw-semibold mb-3 d-flex align-items-center">
                    <i className="bi bi-lock text-success me-2"></i>
                    Your Bookmarks & Content
                  </h3>
                  <ul className="list-unstyled mb-0">
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2 flex-shrink-0"></i>
                      <span>Bookmark URLs, titles, and descriptions</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2 flex-shrink-0"></i>
                      <span>Collections and tags you create</span>
                    </li>
                    <li className="d-flex align-items-start">
                      <i className="bi bi-exclamation-triangle-fill text-warning me-2 flex-shrink-0"></i>
                      <span className="fw-medium">
                        Important: Encrypted bookmarks are completely private -
                        we cannot access them!
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="col-12">
                <div className="bg-purple bg-opacity-10 rounded-3 p-4">
                  <h3 className="h5 fw-semibold mb-3 d-flex align-items-center">
                    <i className="bi bi-server text-purple me-2"></i>
                    Technical Information
                  </h3>
                  <ul className="list-unstyled mb-0">
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2 flex-shrink-0"></i>
                      <span>
                        Browser type and version (to ensure compatibility)
                      </span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2 flex-shrink-0"></i>
                      <span>
                        IP address (for security and regional compliance)
                      </span>
                    </li>
                    <li className="d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-success me-2 flex-shrink-0"></i>
                      <span>Usage patterns (to improve our service)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* How We Use Your Data */}
          <section id="how-we-use" className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-globe fs-3 text-primary me-3"></i>
              How We Use Your Information
            </h2>

            <div className="row g-3">
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body">
                    <h3 className="h5 fw-semibold mb-3">
                      To Provide Our Service
                    </h3>
                    <ul className="list-unstyled text-muted mb-0">
                      <li>• Sync bookmarks across devices</li>
                      <li>• Power AI categorization</li>
                      <li>• Enable search functionality</li>
                      <li>• Manage your account</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body">
                    <h3 className="h5 fw-semibold mb-3">
                      To Improve onBookmarks
                    </h3>
                    <ul className="list-unstyled text-muted mb-0">
                      <li>• Analyze usage patterns</li>
                      <li>• Fix bugs and issues</li>
                      <li>• Develop new features</li>
                      <li>• Enhance performance</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body">
                    <h3 className="h5 fw-semibold mb-3">For Security</h3>
                    <ul className="list-unstyled text-muted mb-0">
                      <li>• Prevent fraud and abuse</li>
                      <li>• Protect against attacks</li>
                      <li>• Ensure data integrity</li>
                      <li>• Comply with legal requirements</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card border-danger h-100">
                  <div className="card-body">
                    <h3 className="h5 fw-semibold mb-3">What We DON'T Do</h3>
                    <ul className="list-unstyled text-danger mb-0">
                      <li>✗ Sell your data to anyone</li>
                      <li>✗ Share data with advertisers</li>
                      <li>✗ Read your encrypted bookmarks</li>
                      <li>✗ Use data for targeted ads</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Encryption & Security */}
          <section id="encryption" className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-lock fs-3 text-success me-3"></i>
              Zero-Knowledge Encryption
            </h2>

            <div className="bg-success bg-opacity-10 rounded-3 p-4 p-lg-5">
              <h3 className="h5 fw-semibold mb-2">
                Your Privacy is Guaranteed
              </h3>
              <p className="text-muted mb-4">
                When you encrypt a bookmark, it's protected with AES-GCM
                encryption before leaving your device. Your encryption keys
                never touch our servers.
              </p>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="bg-white bg-opacity-75 rounded-3 p-3">
                    <h4 className="h6 fw-medium mb-2">What This Means:</h4>
                    <ul className="list-unstyled text-muted mb-0">
                      <li>✓ Only you can decrypt your data</li>
                      <li>✓ We can't read encrypted bookmarks</li>
                      <li>✓ Government requests can't expose encrypted data</li>
                      <li>✓ True end-to-end encryption</li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="bg-white bg-opacity-75 rounded-3 p-3">
                    <h4 className="h6 fw-medium mb-2">Your Responsibility:</h4>
                    <ul className="list-unstyled text-muted mb-0">
                      <li>• Keep your encryption keys safe</li>
                      <li>• We cannot recover lost keys</li>
                      <li>• Use key hints to remember</li>
                      <li>• Consider secure key backup</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* GDPR Compliance */}
          <section id="gdpr" className="mb-5">
            <h2 className="h3 fw-bold mb-2 d-flex align-items-center">
              <i className="bi bi-person-check fs-3 text-primary me-3"></i>
              GDPR Compliance (European Users)
            </h2>
            <p className="text-muted mb-4">
              If you're in the European Economic Area (EEA), UK, or Switzerland,
              you have additional rights under GDPR:
            </p>

            <div className="bg-primary bg-opacity-10 rounded-3 p-4 p-lg-5">
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <h3 className="h5 fw-semibold mb-3">Your GDPR Rights</h3>
                      <ul className="list-unstyled text-muted">
                        <li className="d-flex align-items-start mb-2">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          <span>
                            <strong>Access:</strong> Request a copy of your data
                          </span>
                        </li>
                        <li className="d-flex align-items-start mb-2">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          <span>
                            <strong>Rectification:</strong> Correct inaccurate
                            data
                          </span>
                        </li>
                        <li className="d-flex align-items-start mb-2">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          <span>
                            <strong>Erasure:</strong> Delete your account and
                            data
                          </span>
                        </li>
                        <li className="d-flex align-items-start mb-2">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          <span>
                            <strong>Portability:</strong> Export your data
                          </span>
                        </li>
                        <li className="d-flex align-items-start">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          <span>
                            <strong>Objection:</strong> Opt-out of processing
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <h3 className="h5 fw-semibold mb-3">
                        Legal Basis for Processing
                      </h3>
                      <ul className="list-unstyled text-muted">
                        <li className="mb-3">
                          <strong>Contract:</strong> To provide the service you
                          requested
                        </li>
                        <li className="mb-3">
                          <strong>Consent:</strong> For optional features and
                          communications
                        </li>
                        <li className="mb-3">
                          <strong>Legitimate Interest:</strong> For security and
                          improvements
                        </li>
                        <li>
                          <strong>Legal Obligation:</strong> To comply with laws
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-primary bg-opacity-10 rounded">
                <p className="small text-primary mb-0">
                  <strong>Data Protection Officer:</strong> For GDPR inquiries,
                  contact our DPO at{" "}
                  <a
                    href="mailto:dpo@onbookmarks.com"
                    className="text-decoration-underline"
                  >
                    support@onbookmarks.com
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* COPPA Compliance */}
          <section id="coppa" className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-people fs-3 text-purple me-3"></i>
              Children's Privacy (COPPA Compliance)
            </h2>

            <div className="bg-purple bg-opacity-10 rounded-3 p-4 p-lg-5">
              <div className="row align-items-start g-3">
                <div className="col">
                  <h3 className="h5 fw-semibold mb-3">
                    Age Requirement: 13 Years or Older
                  </h3>
                  <p className="text-muted mb-4">
                    onBookmarks is not intended for children under 13 years of
                    age. We do not knowingly collect personal information from
                    children under 13.
                  </p>

                  <div className="card mb-3">
                    <div className="card-body">
                      <h4 className="h6 fw-medium mb-3">
                        For Parents & Guardians:
                      </h4>
                      <ul className="list-unstyled text-muted mb-0">
                        <li>
                          • If your child under 13 has created an account,
                          please contact us immediately
                        </li>
                        <li>
                          • We will promptly delete the account and all
                          associated data
                        </li>
                        <li>• We support parental rights under COPPA</li>
                        <li>
                          • Contact:{" "}
                          <a
                            href="mailto:privacy@onbookmarks.com"
                            className="text-purple text-decoration-underline"
                          >
                            privacy@onbookmarks.com
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-3 bg-purple bg-opacity-10 rounded">
                    <p className="small text-purple mb-0">
                      <strong>Educational Use:</strong> Teachers using
                      onBookmarks in classrooms with students under 13 must
                      create and manage accounts on behalf of students,
                      maintaining full control.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section id="your-rights" className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-shield fs-3 text-primary me-3"></i>
              Your Privacy Rights
            </h2>

            <div className="mb-4">
              <div className="bg-primary bg-opacity-10 rounded-3 p-4">
                <h3 className="h5 fw-semibold mb-4">
                  Universal Rights (All Users)
                </h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="bg-white bg-opacity-75 rounded-3 p-3">
                      <h4 className="h6 fw-medium mb-2 d-flex align-items-center">
                        <i className="bi bi-eye text-primary me-2"></i>
                        Access Your Data
                      </h4>
                      <p className="small text-muted mb-0">
                        Download all your data anytime from account settings
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="bg-white bg-opacity-75 rounded-3 p-3">
                      <h4 className="h6 fw-medium mb-2 d-flex align-items-center">
                        <i className="bi bi-database text-primary me-2"></i>
                        Delete Everything
                      </h4>
                      <p className="small text-muted mb-0">
                        Permanently remove your account and all data
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="bg-white bg-opacity-75 rounded-3 p-3">
                      <h4 className="h6 fw-medium mb-2 d-flex align-items-center">
                        <i className="bi bi-envelope text-primary me-2"></i>
                        Control Communications
                      </h4>
                      <p className="small text-muted mb-0">
                        Choose which emails you receive or opt-out entirely
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="bg-white bg-opacity-75 rounded-3 p-3">
                      <h4 className="h6 fw-medium mb-2 d-flex align-items-center">
                        <i className="bi bi-lock text-primary me-2"></i>
                        Update Information
                      </h4>
                      <p className="small text-muted mb-0">
                        Change your profile and preferences anytime
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-light rounded-3 p-4">
              <h3 className="h5 fw-semibold mb-4">
                Additional Rights by Region
              </h3>
              <div className="row g-3">
                <div className="col-md-6">
                  <h4 className="h6 fw-medium mb-2">
                    🇪🇺 European Union (GDPR)
                  </h4>
                  <p className="small text-muted">
                    Right to rectification, erasure, restriction, portability,
                    and objection
                  </p>
                </div>
                <div className="col-md-6">
                  <h4 className="h6 fw-medium mb-2">
                    🇺🇸 California (CCPA/CPRA)
                  </h4>
                  <p className="small text-muted">
                    Right to know, delete, opt-out, and non-discrimination
                  </p>
                </div>
                <div className="col-md-6">
                  <h4 className="h6 fw-medium mb-2">
                    🇬🇧 United Kingdom (UK GDPR)
                  </h4>
                  <p className="small text-muted">
                    Similar rights to EU GDPR with UK-specific provisions
                  </p>
                </div>
                <div className="col-md-6">
                  <h4 className="h6 fw-medium mb-2">🇨🇦 Canada (PIPEDA)</h4>
                  <p className="small text-muted">
                    Right to access, correct, and control consent
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Cookies & Tracking */}
          <section id="cookies" className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-cookie fs-3 text-warning me-3"></i>
              Cookies & Tracking
            </h2>

            <div className="bg-warning bg-opacity-10 rounded-3 p-4 p-lg-5">
              <div className="row g-4">
                <div className="col-md-6">
                  <h3 className="h5 fw-semibold mb-3">
                    Essential Cookies Only
                  </h3>
                  <p className="text-muted mb-3">
                    We use only essential cookies required for the service to
                    function:
                  </p>
                  <ul className="list-unstyled text-muted">
                    <li>• Authentication (keeping you logged in)</li>
                    <li>• Security (protecting against attacks)</li>
                    <li>• Preferences (remembering your settings)</li>
                  </ul>
                </div>

                <div className="col-md-6">
                  <h3 className="h5 fw-semibold mb-3">Analytics & Privacy</h3>
                  <p className="text-muted mb-3">
                    We use privacy-focused analytics:
                  </p>
                  <ul className="list-unstyled text-muted">
                    <li>• Google Analytics with IP anonymization</li>
                    <li>• No cross-site tracking</li>
                    <li>• No advertising cookies</li>
                    <li>• Respects Do Not Track signals</li>
                  </ul>
                </div>
              </div>

              <div className="mt-4 p-3 bg-warning bg-opacity-10 rounded">
                <p className="small text-warning-emphasis mb-0">
                  <strong>Browser Extensions:</strong> Our extensions only
                  access sites when you actively bookmark them. See our{" "}
                  <Link
                    to="/legal/extension-privacy-policy"
                    className="text-decoration-underline"
                  >
                    Extension Privacy Policy
                  </Link>{" "}
                  for details.
                </p>
              </div>
            </div>
          </section>

          {/* Data Sharing */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-globe fs-3 text-primary me-3"></i>
              When We Share Information
            </h2>

            <div className="row g-3">
              <div className="col-12">
                <div className="bg-success bg-opacity-10 rounded-3 p-4">
                  <h3 className="h5 fw-semibold mb-2 d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    Service Providers (Limited Access)
                  </h3>
                  <p className="text-muted mb-0">
                    We work with trusted providers who help run our service
                    (like Digital Ocean for hosting). They only access what's
                    necessary and are bound by strict confidentiality
                    agreements.
                  </p>
                </div>
              </div>

              <div className="col-12">
                <div className="bg-warning bg-opacity-10 rounded-3 p-4">
                  <h3 className="h5 fw-semibold mb-2 d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                    Legal Requirements
                  </h3>
                  <p className="text-muted mb-0">
                    We may disclose information if legally required, but we'll
                    always try to notify you unless prohibited by law. Remember:
                    we can't decrypt your encrypted bookmarks even if asked.
                  </p>
                </div>
              </div>

              <div className="col-12">
                <div className="bg-danger bg-opacity-10 rounded-3 p-4">
                  <h3 className="h5 fw-semibold mb-2 d-flex align-items-center">
                    <i className="bi bi-shield-x text-danger me-2"></i>
                    What We Never Do
                  </h3>
                  <ul className="list-unstyled text-muted mb-0">
                    <li>✗ Sell your personal information</li>
                    <li>✗ Share data for advertising</li>
                    <li>✗ Allow third-party tracking</li>
                    <li>✗ Decrypt your private bookmarks</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Actions */}
          <div className="bg-light rounded-3 p-4 p-lg-5">
            <h3 className="h4 fw-semibold mb-4 text-center">
              Quick Privacy Actions
            </h3>
            <div className="row g-3 justify-content-center">
              <div className="col-md-4">
                <Link
                  to="https://my.onbookmarks.com/settings/account"
                  className="card text-center text-decoration-none h-100 border-0 shadow-sm"
                >
                  <div className="card-body py-4">
                    <i className="bi bi-shield-check fs-2 text-primary d-block mb-2"></i>
                    <p className="fw-medium mb-0">Privacy Settings</p>
                  </div>
                </Link>
              </div>

              <div className="col-md-4">
                <Link
                  to="https://my.onbookmarks.com/settings/data"
                  className="card text-center text-decoration-none h-100 border-0 shadow-sm"
                >
                  <div className="card-body py-4">
                    <i className="bi bi-database-down fs-2 text-success d-block mb-2"></i>
                    <p className="fw-medium mb-0">Export My Data</p>
                  </div>
                </Link>
              </div>

              <div className="col-md-4">
                <Link
                  to="https://my.onbookmarks.com/settings/account"
                  className="card text-center text-decoration-none h-100 border-0 shadow-sm"
                >
                  <div className="card-body py-4">
                    <i className="bi bi-exclamation-triangle fs-2 text-danger d-block mb-2"></i>
                    <p className="fw-medium mb-0">Delete Account</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
